import moment from 'moment';

const FIXED_PRICES = 'pricesFixedForm';
const CATEGORY_PRICES = 'pricesCategoryForm';
const DAILY_PRICES = 'pricesDailyForm';
const FUTURE_PRICES = 'futurePricesForm';

const getParticipants = (
  accommodation,
  currency,
  previousParticipants,
  dates
) => {
  let participants = [];

  if (accommodation.pricesType === FIXED_PRICES) {
    const prices = accommodation[FIXED_PRICES];
    participants.push({
      id: `${currency}-${prices[currency]}`,
      participant: '',
      participants: 0,
      currency: currency,
      currencyValue: prices[currency],
      NOK: prices.NOK,
      EUR: prices.EUR,
      USD: prices.USD
    });
    participants = _setPreviousParticipantValue(
      participants,
      previousParticipants
    );
    return participants;
  }

  if (
    accommodation.pricesType === FUTURE_PRICES &&
    dates?.startDate &&
    dates?.endDate
  ) {
    const start = moment(dates.startDate);
    const end = moment(dates.endDate);
    const futurePrices = accommodation.futurePricesFormDisplay || [];

    

    let hasMatchingFuturePrice = false;

    futurePrices.forEach(priceSet => {
      const priceStart = moment(priceSet.startDate);
      const priceEnd = moment(priceSet.endDate);

      const isMatching =
        start.isBetween(priceStart, priceEnd, null, '[]') ||
        end.isBetween(priceStart, priceEnd, null, '[]') ||
        (start.isSameOrBefore(priceStart) && end.isSameOrAfter(priceEnd));

      

      if (isMatching) {
        hasMatchingFuturePrice = true;
        const priceValue = priceSet.prices[currency];

        

        participants.push({
          id: `${currency}-${priceValue}`,
          participant: '',
          participants: 0,
          currency,
          currencyValue: priceValue,
          NOK: priceSet.prices.NOK,
          EUR: priceSet.prices.EUR,
          USD: priceSet.prices.USD,
          ratePlanId: priceSet.ratePlanId,
          ratePlanTitle: priceSet.ratePlanTitle,
          isFuturePricing: true // Flag to indicate future pricing is used
        });
      }
    });

    if (!hasMatchingFuturePrice) {
      
      const otherPrices = [];
      const baseType = accommodation.basePricesType;
      const categories = accommodation[CATEGORY_PRICES]?.categories || [];
      const fixed = accommodation[FIXED_PRICES];

      

      // Helper to try Daily
      const tryDailyFallback = () => {
        if (
          dates?.startDate &&
          dates?.endDate &&
          accommodation?.pricesDailyForm?.days?.length > 0
        ) {
          const start = moment(dates.startDate);
          const end = moment(dates.endDate).subtract(1, 'd');
          let value = 0;
          const dailyPrices = accommodation.pricesDailyForm.days;

          for (
            let date = moment(start);
            date.isSameOrBefore(end);
            date.add(1, 'days')
          ) {
            const day = date.format('ddd');
            dailyPrices.forEach(dailyPrice => {
              if (dailyPrice.day === day && dailyPrice[currency]) {
                value += +dailyPrice[currency];
              }
            });
          }

          if (value > 0) {
            
            return {
              id: `${currency}-${value}`,
              participant: '',
              participants: 0,
              currency,
              currencyValue: value,
              NOK: currency === 'NOK' ? value : 0,
              EUR: currency === 'EUR' ? value : 0,
              USD: currency === 'USD' ? value : 0,
              title: 'Other Prices',
              isFallbackPricing: true,
              fallbackType: 'daily'
            };
          }
        }
        return null;
      };

      // Helper to try Fixed
      const tryFixedFallback = () => {
        if (fixed && Number(fixed[currency]) > 0) {
          
          return {
            id: `${currency}-${fixed[currency]}`,
            participant: '',
            participants: 0,
            currency,
            currencyValue: fixed[currency],
            NOK: fixed.NOK,
            EUR: fixed.EUR,
            USD: fixed.USD,
            title: 'Other Prices',
            isFallbackPricing: true,
            fallbackType: 'fixed'
          };
        }
        return null;
      };

      // Helper to try Category
      const tryCategoryFallback = () => {
        if (categories.length > 0) {
          const validCategories = categories.filter(category => {
            const priceValue = category[currency];
            return priceValue && Number(priceValue) > 0;
          });

          if (validCategories.length === 0) return null;

          
          return validCategories.map(category => ({
            id: `${currency}-${category[currency]}`,
            participant: category.participant,
            participants: 0,
            currency,
            currencyValue: category[currency],
            NOK: category.NOK,
            EUR: category.EUR,
            USD: category.USD,
            title: 'Other Prices',
            isFallbackPricing: true,
            fallbackType: 'category'
          }));
        }
        return null;
      };

      // --- Fallback Selection Logic ---
      
      let foundFallback = false;

      // 1. Primary Priority: Follow explicit baseType if detected
      if (baseType === DAILY_PRICES) {
        const res = tryDailyFallback();
        if (res) {
          otherPrices.push(res);
          foundFallback = true;
        }
      } else if (baseType === FIXED_PRICES) {
        const res = tryFixedFallback();
        if (res) {
          otherPrices.push(res);
          foundFallback = true;
        }
      } else if (baseType === CATEGORY_PRICES) {
        const res = tryCategoryFallback();
        if (res) {
          otherPrices.push(...res);
          foundFallback = true;
        }
      }

      // 2. Secondary Priority: Use existing patterns if baseType detection failed or data missing
      if (!foundFallback) {
        
        
        const daily = tryDailyFallback();
        if (daily) {
          otherPrices.push(daily);
          foundFallback = true;
        } else {
          // If no daily, try Fixed then Category (preserving traditional priority)
          const fixedRes = tryFixedFallback();
          if (fixedRes) {
            otherPrices.push(fixedRes);
            foundFallback = true;
          } else {
            const catRes = tryCategoryFallback();
            if (catRes) {
              otherPrices.push(...catRes);
              foundFallback = true;
            }
          }
        }
      }

      participants = [...participants, ...otherPrices];
    }

    participants = _setPreviousParticipantValue(
      participants,
      previousParticipants
    );

    return participants;
  }

  if (accommodation.pricesType === DAILY_PRICES) {
    if (dates && dates.startDate && dates.endDate) {
      const dailyPrices = accommodation.pricesDailyForm.days;
      const start = dates.startDate;
      const end = moment(dates.endDate).subtract(1, 'd');
      let value = 0;

      for (
        let date = moment(start);
        date.isSameOrBefore(end);
        date.add(1, 'days')
      ) {
        const day = date.format('ddd');
        dailyPrices.forEach(dailyPrice => {
          if (dailyPrice.day === day && dailyPrice[currency]) {
            value += +dailyPrice[currency];
          }
        });
      }

      participants.push({
        id: `${currency}-${value}`,
        participant: '',
        participants: 0,
        currency: currency,
        currencyValue: value,
        NOK: currency === 'NOK' ? value : 0,
        EUR: currency === 'EUR' ? value : 0,
        USD: currency === 'USD' ? value : 0
      });
    }
  } else {
    const categories = accommodation[CATEGORY_PRICES].categories;
    categories.forEach(item => {
      participants.push({
        id: `${item.participant}-${currency}-${item[currency]}`,
        participant: item.participant,
        participants: 0,
        currency: currency,
        currencyValue: item[currency],
        NOK: item.NOK,
        EUR: item.EUR,
        USD: item.USD
      });
    });
  }

  participants = _setPreviousParticipantValue(
    participants,
    previousParticipants
  );

  return participants;
};

const _setPreviousParticipantValue = (participants, previousParticipants) => {
  return participants.map(item => {
    const theSameParticipant = previousParticipants
      ? previousParticipants.find(
          participant => participant.participant === item.participant
        )
      : false;
    if (theSameParticipant) {
      return {
        ...item,
        participants: theSameParticipant.participants
      };
    }
    return item;
  });
};

export { getParticipants };
